import {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {saveAs} from 'file-saver';
import {useIntl} from 'react-intl';
import {
  Button as BootstrapButton,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
} from 'react-bootstrap';
import TableMetronic from '../../../../../app/common/components/table/TableMetronic';
import TabComponent from '../../../../../app/common/components/TabComponent';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import ActionIcon from '../../../../../Components/ActionIcon';
import Button from '../../../../../Components/Button';
import {
  AnnuaireContact,
  AnnuaireInterneEntry,
  LDAP_DESCRIPTIONS,
  LDAP_OPTIONS,
} from '../TrunkSip/mocks';
import {useGetAnnuaire, useGetAnnuaireInterne} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import Loading from '../../../../../app/common/components/Loading';

/** Blocs d'informations complémentaires par marque de téléphone, affichés dans l'onglet LDAP. */
const INFOS_TELEPHONES = [
  {
    titreId: 'TELEPHONIE.ANNUAIRE.LDAP.INFOS.SNOM_TITLE',
    ligneIds: [
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.COMPATIBLE_MODELS_LINE',
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.SNOM_LINE_2',
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.SNOM_LINE_3',
    ],
  },
  {
    titreId: 'TELEPHONIE.ANNUAIRE.LDAP.INFOS.YEALINK_TITLE',
    ligneIds: [
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.COMPATIBLE_MODELS_LINE',
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.YEALINK_LINE_2',
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.YEALINK_LINE_3',
    ],
  },
  {
    titreId: 'TELEPHONIE.ANNUAIRE.LDAP.INFOS.GIGASET_TITLE',
    ligneIds: [
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.GIGASET_LINE_1',
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.GIGASET_LINE_2',
      'TELEPHONIE.ANNUAIRE.LDAP.INFOS.GIGASET_LINE_3',
    ],
  },
];

/** Numéro court interne : exactement 4 chiffres. */
const LDAP_OPTION_LABEL_KEYS: Record<string, string> = {
  'LDAP CELESTE': 'TELEPHONIE.ANNUAIRE.LDAP.OPTION.CELESTE',
  'LDAP Externe': 'TELEPHONIE.ANNUAIRE.LDAP.OPTION.EXTERNE',
  'LDAP Neutre': 'TELEPHONIE.ANNUAIRE.LDAP.OPTION.NEUTRE',
  'LDAP Inactif': 'TELEPHONIE.ANNUAIRE.LDAP.OPTION.INACTIF',
};

const LDAP_DESCRIPTION_KEYS: Record<string, {texteId: string; alerteId?: string}> = {
  'LDAP CELESTE': {texteId: 'TELEPHONIE.ANNUAIRE.LDAP.DESC.CELESTE.TEXTE'},
  'LDAP Externe': {texteId: 'TELEPHONIE.ANNUAIRE.LDAP.DESC.EXTERNE.TEXTE'},
  'LDAP Neutre': {texteId: 'TELEPHONIE.ANNUAIRE.LDAP.DESC.NEUTRE.TEXTE'},
  'LDAP Inactif': {
    texteId: 'TELEPHONIE.ANNUAIRE.LDAP.DESC.INACTIF.TEXTE',
    alerteId: 'TELEPHONIE.ANNUAIRE.LDAP.DESC.INACTIF.ALERTE',
  },
};

const RACCOURCI_REGEX = /^\d{4}$/;
/** Numéro de téléphone au format international (ex. +41220000000). */
const NUMERO_INTERNATIONAL_REGEX = /^\+\d{6,15}$/;

/** Une ligne "Nom,Numéro,Raccourci" (ou séparée par ;) — nom et numéro obligatoires, raccourci optionnel. */
const parseLigneContact = (ligne: string): Omit<AnnuaireContact, 'id'> | null => {
  const parts = ligne
    .trim()
    .replace(/;/g, ',')
    .split(',')
    .map((part) => part.trim());
  const [nom, numero = '', raccourci = ''] = parts;
  if (
    !nom ||
    !NUMERO_INTERNATIONAL_REGEX.test(numero) ||
    (raccourci !== '' && !RACCOURCI_REGEX.test(raccourci))
  ) {
    return null;
  }
  return {nom, numero, raccourci};
};

const AnnuaireExterne = (): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading} = useGetAnnuaire();
  const [contactsLocal, setContactsLocal] = useState<AnnuaireContact[] | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [contactEnEdition, setContactEnEdition] = useState<AnnuaireContact | null>(null);
  const [contactASupprimer, setContactASupprimer] = useState<AnnuaireContact | null>(null);
  const [confirmerSupprimerTous, setConfirmerSupprimerTous] = useState<boolean>(false);
  const [showImportExport, setShowImportExport] = useState<boolean>(false);
  const [message, setMessage] = useAutoDismissMessage();
  const [nom, setNom] = useState('');
  const [numero, setNumero] = useState('');
  const [raccourci, setRaccourci] = useState('');

  const inputImportRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data) {
      setContactsLocal(data.contacts);
    }
  }, [data]);

  const contacts = useMemo(() => contactsLocal ?? data?.contacts ?? [], [contactsLocal, data]);

  const numeroValide = NUMERO_INTERNATIONAL_REGEX.test(numero.trim());
  const raccourciValide = raccourci.trim() === '' || RACCOURCI_REGEX.test(raccourci.trim());
  const formulaireValide = nom.trim() !== '' && numeroValide && raccourciValide;

  const ouvrirAjout = (): void => {
    setContactEnEdition(null);
    setNom('');
    setNumero('');
    setRaccourci('');
    setMessage(null);
    setShowForm(true);
  };

  const ouvrirModification = (contact: AnnuaireContact): void => {
    setContactEnEdition(contact);
    setNom(contact.nom);
    setNumero(contact.numero);
    setRaccourci(contact.raccourci);
    setMessage(null);
    setShowForm(true);
  };

  const fermerFormulaire = (): void => {
    setShowForm(false);
    setContactEnEdition(null);
  };

  const enregistrerContact = (): void => {
    if (contactEnEdition) {
      setContactsLocal(
        contacts.map((contact) =>
          contact.id === contactEnEdition.id
            ? {
                ...contact,
                nom: nom.trim(),
                numero: numero.trim(),
                raccourci: raccourci.trim(),
              }
            : contact
        )
      );
    } else {
      const nouvelId = Math.max(0, ...contacts.map((contact) => contact.id)) + 1;
      setContactsLocal([
        ...contacts,
        {
          id: nouvelId,
          nom: nom.trim(),
          numero: numero.trim(),
          raccourci: raccourci.trim(),
        },
      ]);
    }
    fermerFormulaire();
    setMessage(intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MESSAGE.SUCCESS'}));
  };

  const confirmerSuppression = (): void => {
    if (contactASupprimer) {
      setContactsLocal(contacts.filter((contact) => contact.id !== contactASupprimer.id));
      setMessage(intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MESSAGE.SUCCESS'}));
    }
    setContactASupprimer(null);
  };

  const confirmerSuppressionTousLesContacts = (): void => {
    setContactsLocal([]);
    setConfirmerSupprimerTous(false);
    setMessage(intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MESSAGE.SUCCESS'}));
  };

  const importerListeContacts = (fichier: File): void => {
    const lecteur = new FileReader();
    lecteur.onload = () => {
      const lignes = String(lecteur.result ?? '')
        .split(/\r?\n/)
        .map((ligne) => ligne.trim())
        .filter((ligne) => ligne !== '');
      const nouveauxContacts = lignes
        .map((ligne) => parseLigneContact(ligne))
        .filter((contact): contact is Omit<AnnuaireContact, 'id'> => contact != null);
      if (nouveauxContacts.length === 0) {
        setMessage(intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT.NONE_VALID'}));
        return;
      }
      let prochainId = Math.max(0, ...contacts.map((c) => c.id)) + 1;
      const contactsAvecId = nouveauxContacts.map((contact) => ({...contact, id: prochainId++}));
      setContactsLocal([...contacts, ...contactsAvecId]);
      setMessage(
        intl.formatMessage(
          {id: 'TELEPHONIE.ANNUAIRE.IMPORT.LIST_SUCCESS'},
          {count: contactsAvecId.length}
        )
      );
    };
    lecteur.readAsText(fichier);
  };

  const exporterContacts = (): void => {
    const header = [
      intl.formatMessage({id: 'USER_MANAGER.FORM.NAME'}),
      intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.NUMERO'}),
      intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.RACCOURCI'}),
    ];
    const lignes = contacts.map((contact) => [contact.nom, contact.numero, contact.raccourci]);
    const csv = [header, ...lignes].map((ligne) => ligne.join(';')).join('\n');
    const blob = new Blob([`﻿${csv}`], {type: 'text/csv;charset=utf-8'});
    saveAs(blob, 'annuaire-externe.csv');
  };

  const telechargerModeleCsv = (): void => {
    const header = [
      intl.formatMessage({id: 'USER_MANAGER.FORM.NAME'}),
      intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.NUMERO'}),
      intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.RACCOURCI'}),
    ];
    const blob = new Blob([`﻿${header.join(';')}\n`], {type: 'text/csv;charset=utf-8'});
    saveAs(blob, 'modele-annuaire-externe.csv');
  };

  const columns = useMemo<ColumnDef<AnnuaireContact>[]>(
    () => [
      {header: intl.formatMessage({id: 'USER_MANAGER.FORM.NAME'}), accessorKey: 'nom'},
      {header: intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.NUMERO'}), accessorKey: 'numero'},
      {
        header: intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.RACCOURCI'}),
        accessorKey: 'raccourci',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.TABLE.COLUMN.ACTIONS'}),
        id: 'actions',
        size: 110,
        enableSorting: false,
        cell: (info) => (
          <Dropdown>
            <Dropdown.Toggle variant="secondary" size="sm">
              {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.TABLE.COLUMN.ACTIONS'})}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => ouvrirModification(info.row.original)}>
                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
              </Dropdown.Item>
              <Dropdown.Item
                className="text-danger"
                onClick={() => setContactASupprimer(info.row.original)}
              >
                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contacts, intl.locale]
  );

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  const formulaireModal = (
    <Modal show={showForm} onHide={fermerFormulaire} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {contactEnEdition
            ? intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FORM.TITLE_EDIT'})
            : intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FORM.TITLE_ADD'})}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-5">
          <Col xs={12}>
            <Form.Label className="fs-6">
              {intl.formatMessage({id: 'USER_MANAGER.FORM.NAME'})}
            </Form.Label>
            <Form.Control value={nom} onChange={(e) => setNom(e.target.value)} />
          </Col>
          <Col xs={12}>
            <Form.Label className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.NUMERO'})}
            </Form.Label>
            <Form.Control
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              isInvalid={numero.trim() !== '' && !numeroValide}
            />
            <Form.Text
              className={
                numero.trim() !== '' && !numeroValide ? 'text-danger' : 'text-primary fst-italic'
              }
            >
              {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FORM.NUMERO_HELP'})}
            </Form.Text>
          </Col>
          <Col xs={12}>
            <Form.Label className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.RACCOURCI'})}
            </Form.Label>
            <Form.Control
              value={raccourci}
              onChange={(e) => setRaccourci(e.target.value)}
              isInvalid={raccourci.trim() !== '' && !raccourciValide}
            />
            <Form.Text
              className={
                raccourci.trim() !== '' && !raccourciValide
                  ? 'text-danger'
                  : 'text-primary fst-italic'
              }
            >
              {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FORM.RACCOURCI_HELP'})}
            </Form.Text>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <BootstrapButton variant="secondary" onClick={fermerFormulaire}>
          {intl.formatMessage({id: 'CANCEL'})}
        </BootstrapButton>
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FORM.SAVE'})}
          disabled={!formulaireValide}
          onClick={enregistrerContact}
        />
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="mt-4">
      {message && <div className="fw-bold text-danger mb-3">{message}</div>}

      <input
        ref={inputImportRef}
        type="file"
        accept=".csv,.txt,.xls"
        className="d-none"
        onChange={(e) => {
          const fichier = e.target.files?.[0];
          if (fichier) {
            importerListeContacts(fichier);
          }
          e.target.value = '';
        }}
      />

      <TableMetronic<AnnuaireContact>
        title={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.EXTERNE.TITLE'})}
        data={contacts}
        columns={columns}
        pagination
        smallSearchBar
        elementName={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.TABLE.ELEMENT_NAME'})}
        elementNamePlural={intl.formatMessage({
          id: 'TELEPHONIE.ANNUAIRE.TABLE.ELEMENT_NAME_PLURAL',
        })}
        toolbar={
          <div className="d-flex gap-3">
            <BootstrapButton
              variant="dark"
              size="sm"
              onClick={() => setShowImportExport(true)}
            >
              <i className="bi bi-box-arrow-in-down me-2" />
              {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT'})}
            </BootstrapButton>
            <Button
              label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.EXTERNE.ADD'})}
              faIcon="bi bi-person-plus-fill fs-6 me-2"
              onClick={ouvrirAjout}
            />
          </div>
        }
        toolbarEnd={
          <div className="d-flex align-items-center">
            <ActionIcon
              label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.PRINT_LIST'})}
              faIcon="fs-3 bi bi-printer"
              onClick={() => window.print()}
            />
            <ActionIcon
              label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.DELETE_ALL'})}
              faIcon="fs-3 bi bi-trash text-danger"
              onClick={() => setConfirmerSupprimerTous(true)}
            />
          </div>
        }
      />

      <Modal show={showImportExport} onHide={() => setShowImportExport(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="text-primary">
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-center mb-6">
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.INTRO'})}
          </p>
          <Row className="g-5">
            <Col xs={12} md={6} className="text-center">
              <BootstrapButton
                variant="primary"
                className="mb-4"
                onClick={() => inputImportRef.current?.click()}
              >
                <i className="bi bi-box-arrow-in-down me-2" />
                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.IMPORTER'})}
              </BootstrapButton>
              <ol className="text-start text-muted fs-7 mb-4">
                <li>
                  {intl.formatMessage({
                    id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.IMPORTER_ETAPE_1',
                  })}
                </li>
                <li>
                  {intl.formatMessage({
                    id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.IMPORTER_ETAPE_2',
                  })}
                </li>
              </ol>
              <BootstrapButton variant="primary" size="sm" onClick={telechargerModeleCsv}>
                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.MODELE_CSV'})}
              </BootstrapButton>
            </Col>
            <Col xs={12} md={6} className="text-center">
              <BootstrapButton variant="primary" className="mb-4" onClick={exporterContacts}>
                <i className="bi bi-box-arrow-in-down me-2" />
                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.EXPORTER'})}
              </BootstrapButton>
              <p className="text-muted fs-7">
                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.EXPORTER_DESC'})}
              </p>
            </Col>
          </Row>
          {message && <div className="fw-bold text-danger mt-4 text-center">{message}</div>}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setShowImportExport(false)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="primary" onClick={() => setShowImportExport(false)}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.IMPORT_EXPORT_MODAL.OK'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={contactASupprimer != null} onHide={() => setContactASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_BODY'},
            {
              contact: contactASupprimer?.nom ?? '',
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setContactASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppression}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={confirmerSupprimerTous} onHide={() => setConfirmerSupprimerTous(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_ALL_BODY'},
            {b: (chunks: ReactNode[]) => <strong>{chunks}</strong>}
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setConfirmerSupprimerTous(false)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppressionTousLesContacts}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.DELETE_ALL_CONFIRM'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      {formulaireModal}
    </div>
  );
};

const AnnuaireInterne = (): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading} = useGetAnnuaireInterne();

  const columns = useMemo<ColumnDef<AnnuaireInterneEntry>[]>(
    () => [
      {header: intl.formatMessage({id: 'USER_MANAGER.FORM.NAME'}), accessorKey: 'nom'},
      {
        header: intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.NUMERO'}),
        accessorKey: 'numero',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl.locale]
  );

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  return (
    <TableMetronic<AnnuaireInterneEntry>
      title={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.INTERNE.TITLE'})}
      data={data.entrees}
      columns={columns}
      pagination
      smallSearchBar
      elementName={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.INTERNE.ELEMENT_NAME'})}
      elementNamePlural={intl.formatMessage({
        id: 'TELEPHONIE.ANNUAIRE.INTERNE.ELEMENT_NAME_PLURAL',
      })}
    />
  );
};

interface NumeroServiceEntry {
  code: string;
  descriptionId: string;
  exempleId?: string;
}

/** Numéros courts de services du standard, identiques pour toutes les prestations Centrex. */
const NUMEROS_SERVICE: NumeroServiceEntry[] = [
  {code: '501', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_501'},
  {
    code: '502',
    descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_502',
    exempleId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_502_EXEMPLE',
  },
  {
    code: '503',
    descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_503',
    exempleId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_503_EXEMPLE',
  },
  {code: '510', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_510'},
  {code: '530', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_530'},
  {code: '540', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_540'},
  {code: '560', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_560'},
  {
    code: '561',
    descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_561',
    exempleId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_561_NOTE',
  },
  {code: '570', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_570'},
  {code: '571', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_571'},
  {code: '572', descriptionId: 'TELEPHONIE.ANNUAIRE.SERVICE.CODE_572'},
];

const AnnuaireNumerosService = (): JSX.Element => {
  const intl = useIntl();

  const columns = useMemo<ColumnDef<NumeroServiceEntry>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.FIELD.NUMERO_COURT'}),
        accessorKey: 'code',
        size: 140,
        cell: (info) => (
          <span className="badge bg-dark fs-6">{info.row.original.code}</span>
        ),
      },
      {
        header: intl.formatMessage({id: 'ADMIN.NEWS.FORM.DESCRIPTION'}),
        id: 'description',
        cell: (info) => (
          <div>
            <div>
              {intl.formatMessage(
                {id: info.row.original.descriptionId},
                {i: (chunks: ReactNode[]) => <i>{chunks}</i>}
              )}
              {info.row.original.exempleId && (
                <sup className="text-primary ms-1">*</sup>
              )}
            </div>
            {info.row.original.exempleId && (
              <div className="text-muted fs-7 mt-1">
                {intl.formatMessage(
                  {id: info.row.original.exempleId},
                  {
                    code: (chunks: ReactNode[]) => (
                      <span className="badge bg-dark ms-1 me-1">{chunks}</span>
                    ),
                  }
                )}
              </div>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl.locale]
  );

  return (
    <TableMetronic<NumeroServiceEntry>
      title={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.SERVICE.TITLE'})}
      data={NUMEROS_SERVICE}
      columns={columns}
      searchBar={false}
    />
  );
};

const AnnuaireLdap = (): JSX.Element => {
  const intl = useIntl();
  const [mode, setMode] = useState(LDAP_OPTIONS[0]);
  const [motDePasse, setMotDePasse] = useState('mot-de-passe-exemple');
  const [message, setMessage] = useAutoDismissMessage();

  const annuler = (): void => {
    setMode(LDAP_OPTIONS[0]);
    setMotDePasse('mot-de-passe-exemple');
    setMessage(null);
  };

  return (
    <div className="mt-4">
      {message && <div className="fw-bold text-danger mb-3">{message}</div>}
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.INTRO'})}
      </p>

      <Row className="g-5 mb-5">
        <Col xs={12} lg={6}>
          <Card className="h-100">
            <Card.Header>
              <Card.Title className="m-0">
                <Level3Title
                  title={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.ACTIVATE_TITLE'})}
                />
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="fw-semibold fs-6 mb-4">
                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.OPTIONS_LABEL'})}
              </div>
              {LDAP_DESCRIPTIONS.map((option) => (
                <div key={option.titre} className="mb-4">
                  <div className="fw-semibold fs-6 text-primary mb-1">
                    {LDAP_OPTION_LABEL_KEYS[option.titre]
                      ? intl.formatMessage({id: LDAP_OPTION_LABEL_KEYS[option.titre]})
                      : option.titre}
                  </div>
                  <div className="text-muted fs-6">
                    {LDAP_DESCRIPTION_KEYS[option.titre]
                      ? intl.formatMessage({id: LDAP_DESCRIPTION_KEYS[option.titre].texteId})
                      : option.texte}
                    {option.alerte && (
                      <span className="text-danger fw-semibold">
                        {' '}
                        {LDAP_DESCRIPTION_KEYS[option.titre]?.alerteId
                          ? intl.formatMessage({id: LDAP_DESCRIPTION_KEYS[option.titre].alerteId})
                          : option.alerte}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="h-100">
            <Card.Header>
              <Card.Title className="m-0">
                <Level3Title
                  title={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.CONFIG_TITLE'})}
                />
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-5">
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.MODE_LABEL'})}
                </Form.Label>
                <Form.Select value={mode} onChange={(e) => setMode(e.target.value)}>
                  {LDAP_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {LDAP_OPTION_LABEL_KEYS[option]
                        ? intl.formatMessage({id: LDAP_OPTION_LABEL_KEYS[option]})
                        : option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-5">
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.PASSWORD_LABEL'})}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                />
              </Form.Group>
              <div className="d-flex gap-3 justify-content-end">
                <BootstrapButton variant="secondary" size="sm" onClick={annuler}>
                  {intl.formatMessage({id: 'CANCEL'})}
                </BootstrapButton>
                <Button
                  label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.APPLY'})}
                  onClick={() =>
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MESSAGE.SUCCESS'}))
                  }
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <Card.Title className="m-0">
            <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.LDAP.INFOS_TITLE'})} />
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {INFOS_TELEPHONES.map((bloc) => (
            <div key={bloc.titreId} className="mb-5">
              <div className="fw-bold fs-6 mb-2">{intl.formatMessage({id: bloc.titreId})}</div>
              <ul className="text-muted fs-6 mb-0">
                {bloc.ligneIds.map((ligneId) => (
                  <li key={ligneId}>{intl.formatMessage({id: ligneId})}</li>
                ))}
              </ul>
            </div>
          ))}
        </Card.Body>
      </Card>
    </div>
  );
};

const AnnuaireVues = (): JSX.Element => (
  <div className="d-flex flex-column gap-5">
    <AnnuaireInterne />
    <AnnuaireExterne />
    <AnnuaireNumerosService />
  </div>
);

/**
 * MAQUETTE — onglet « Annuaire » d'une prestation Centrex (sous-onglets Annuaire / LDAP).
 */
const Annuaire = (): JSX.Element => {
  const intl = useIntl();
  const [activeKey, setActiveKey] = useState<string>('annuaire');

  return (
    <div className="mt-4">
      <TabComponent
        tabData={[
          {
            title: intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE.ANNUAIRE'}),
            key: 'annuaire',
            content: <AnnuaireVues />,
          },
          {
            title: intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.TAB.LDAP'}),
            key: 'ldap',
            content: <AnnuaireLdap />,
          },
        ]}
        activeKey={activeKey}
        onTabChange={setActiveKey}
      />
    </div>
  );
};

export default Annuaire;
